<template>
  <v-dialog
    v-model="dialogs.importExams"
    max-width="500px"
    @click:outside="hideDialog"
    @keydown.enter="importData"
  >
    <v-card>
      <v-card-title>
        <span>{{ $t(`importExamsDialog.${importing ? 'importingTitle' : 'title'}`) }}</span>
      </v-card-title>
      <v-card-text>
        <v-container
          fluid
          class="pa-0"
        >
          <p
            class="text-subtitle-1 text-center mb-5"
            v-html="$t('importExamsDialog.text')"
          />
          <p
            v-if="!!uploadTemplatePath"
            id="upload-template-link"
            class="text-subtitle-1 text-center mb-5"
            v-html="$t('importExamsDialog.linkText', [uploadTemplatePath])"
          />
          <p
            class="text-subtitle-1 text-center mb-5"
            v-html="$t('importExamsDialog.fields.file.hint')"
          />
          <v-img
            src="@/assets/import-illustration.svg"
            max-width="300px"
            class="mx-auto my-5"
            alt="Spreadsheet"
          />
          <v-divider />
          <v-form ref="form">
            <v-file-input
              ref="uploader"
              v-model="fields.file"
              required
              :rules="rules.file"
              accept=".xlsx"
              prepend-icon="$vuetify.icons.fileExcel"
              single-line
              :label="$t('importExamsDialog.fields.file.label')"
              persistent-hint
            />
            <v-text-field
              v-model="fields.password"
              :type="passwordVisible ? 'text' : 'password'"
              prepend-icon="$vuetify.icons.password"
              :append-icon="passwordVisible ? '$vuetify.icons.passwordShow' : '$vuetify.icons.passwordHide'"
              :hint="$t('importExamsDialog.fields.password.hint')"
              persistent-hint
              @click:append="passwordVisible = !passwordVisible"
            >
              <template #label>
                {{ $t('importExamsDialog.fields.password.label') }}
              </template>
            </v-text-field>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions v-if="!importing">
        <v-spacer />
        <v-btn
          outlined
          depressed
          @click="hideDialog"
        >
          {{ $t('importExamsDialog.cancelAction') }}
        </v-btn>
        <v-btn
          color="primary"
          depressed
          @click="importData"
        >
          {{ $t('importExamsDialog.importAction') }}<v-icon
            right
            x-small
          >
            $vuetify.icons.importAction
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';

const convertObjectToFormData = (object) => {
  const formData = new FormData();
  Object.entries(object).forEach(([key, value]) => formData.append(key, value));
  return formData;
};

export default {
  inject: ['environment', 'imports'],
  data: (vm) => ({
    importing: false,
    passwordVisible: false,
    fields: {
      file: null,
      password: ''
    },
    rules: {
      file: [
        (v) => !!v || vm.$t('importExamsDialog.fields.file.errors.required')
      ]
    }
  }),

  computed: {
    ...mapGetters('dialogs', {
      dialogs: 'get'
    }),

    uploadTemplatePath() {
      return this.environment.getExamsUploadTemplateUrl();
    }
  },

  methods: {
    ...mapActions('dialogs', {
      hideImportExamsDialog: 'HIDE_IMPORT_EXAMS'
    }),
    ...mapMutations('notice', {
      addNotice: 'add'
    }),

    async importData() {
      if (this.validateForm()) {
        this.importing = true;

        try {
          const multipartFormData = convertObjectToFormData(this.fields)
          const response = await this.imports.uploadExamsExcel(multipartFormData)
          if (response.status < 200 || response.status >= 300) {
            throw response;
          }

          await this.$router.push({
            name: 'imported-exams',
            params: {
              initialExams: response?.data?.exams
            }
          });
          this.hideDialog()
        } catch(errResponse) {
          this.addNotice({
            message: this.$t('importExamsDialog.errorUploadingExams', [errResponse?.data?.message]),
            type: 'error'
          });
        } finally {
          this.importing = false
        }
      }
    },

    hideDialog() {
      this.hideImportExamsDialog();
      this.resetDialog();
    },

    resetDialog() {
      this.importing = false;
      this.passwordVisible = false;

      this.resetForm();
    },

    resetForm() {
      // If the dialog is already hidden (when this is called by hideDialog), $refs.form does not exist
      this.$refs.form?.reset();
    },

    validateForm() {
      return this.$refs.form.validate();
    }
  }
};
</script>
